/**
 * Per-record PoR page layout editor (inside a Plan of Record workspace).
 */
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { BookOpen, RotateCcw } from 'lucide-react';
import { PageHeader } from '../prioritization/PageHeader';
import { PorPageLayoutForm } from './PorPageLayoutForm';
import { usePorPageSettingsStore } from '../../stores/prioritization/porPageSettingsStore';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { usePlanInstrumentNav } from '../../contexts/PlanInstrumentNavContext';
import {
  POR_PAGE_REGISTRY,
  resetPorRecordPageLayoutToWorkspace,
  type PorPageKey,
} from '../../lib/porPageSettingsService';

export function PorPageLayoutEditor({
  porId,
  title,
}: {
  porId: string;
  title: string;
}) {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const { porHref } = usePlanInstrumentNav();
  const fetchRecordSettings = usePorPageSettingsStore((s) => s.fetchRecordSettings);
  // Must subscribe to the rows (not only store methods) or checkbox toggles never re-render.
  const workspaceSettings = usePorPageSettingsStore((s) => s.workspaceSettings);
  const recordSettings = usePorPageSettingsStore((s) => s.recordSettings);
  const toggleVisible = usePorPageSettingsStore((s) => s.toggleVisible);
  const loading = usePorPageSettingsStore((s) => s.loading);
  const error = usePorPageSettingsStore((s) => s.error);
  const layoutSource = usePorPageSettingsStore((s) => s.layoutSource);

  useEffect(() => {
    if (workspaceId && porId) void fetchRecordSettings(porId, workspaceId);
  }, [workspaceId, porId, fetchRecordSettings]);

  // Record rows win when present; otherwise workspace defaults (missing key = visible).
  const layoutRows = recordSettings.length > 0 ? recordSettings : workspaceSettings;
  const pageIsVisible = (key: PorPageKey) =>
    layoutRows.find((r) => r.pageKey === key)?.isVisible !== false;
  const onlyOneVisible =
    POR_PAGE_REGISTRY.filter((p) => pageIsVisible(p.key)).length <= 1;

  const handleReset = async () => {
    if (!workspaceId) return;
    const { error: resetError } = await resetPorRecordPageLayoutToWorkspace(porId, workspaceId);
    if (resetError) {
      usePorPageSettingsStore.setState({ error: resetError });
      return;
    }
    // Invalidate cache so fetch reloads post-reset rows.
    usePorPageSettingsStore.setState({ loadedPorId: null, recordSettings: [] });
    void fetchRecordSettings(porId, workspaceId);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<BookOpen className="h-4 w-4 text-[var(--text-sec)]" />}
        iconBg="bg-[var(--hover-tint)]"
        title="Page layout"
        subtitle={`Choose which pages appear in “${title}”. This layout applies only to this Plan of Record.`}
        actions={
          <Link
            to={porHref(porId, '')}
            className="text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            ← Back to record
          </Link>
        }
      />

      {error && (
        <div className="border border-[var(--coral)] bg-[var(--coral-dim)] px-4 py-2.5 text-sm text-[var(--coral)]">
          {error}
        </div>
      )}

      <div className="border border-[var(--slate)] bg-[var(--slate-dim)] px-4 py-3 text-sm text-[var(--text-sec)] space-y-2">
        <p>
          Layout source: <strong>{layoutSource()}</strong>. New Plans of Record copy the workspace
          default at creation; changes here affect only this record.
        </p>
        <p className="text-[var(--text-sec)]">Custom pages will be supported in a future release.</p>
        {workspaceId && (
          <button
            type="button"
            onClick={() => void handleReset()}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--text-sec)] hover:text-[var(--text-pri)]"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset to workspace default
          </button>
        )}
      </div>

      {loading ? (
        <div className="card text-sm text-[var(--text-ter)] py-6 text-center">Loading layout…</div>
      ) : workspaceId ? (
        <PorPageLayoutForm
          isVisible={(key) => pageIsVisible(key as PorPageKey)}
          onlyOneVisible={onlyOneVisible}
          onToggle={(key) => {
            if (onlyOneVisible && pageIsVisible(key as PorPageKey)) return;
            void toggleVisible(key as PorPageKey, workspaceId, porId);
          }}
        />
      ) : null}
    </div>
  );
}
